// @flow

import * as React from 'react';

const useLayoutEffect =
  typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

export const useClickOutside = <T: string>(
  refs: $ReadOnlyArray<{| current: React.ElementRef<T> | null |}>,
  onClickOutside: (MouseEvent | TouchEvent) => void,
) => {
  const handlerRef = React.useRef(onClickOutside);
  const refsRef = React.useRef(refs);

  useLayoutEffect(() => {
    handlerRef.current = onClickOutside;
    refsRef.current = refs;
  });

  React.useEffect(() => {
    const handleStart = (event: MouseEvent | TouchEvent) => {
      const target = event.target;
      if (target instanceof Node) {
        const clickedOutside = refsRef.current.every(
          ref => ref.current && !ref.current.contains(target),
        );

        let tNode = target;
        while (tNode != null) {
          if (
            tNode instanceof HTMLElement &&
            tNode.dataset &&
            tNode.dataset.invisibleForClickOutside != null
          ) {
            // skip elements with data-invisible-for-click-outside="something" attributes
            return;
          }

          tNode = tNode.parentNode;
        }

        if (clickedOutside && handlerRef.current) {
          handlerRef.current(event);
        }
      }
    };

    document.addEventListener('touchstart', handleStart);
    document.addEventListener('mousedown', handleStart);

    return () => {
      document.removeEventListener('touchstart', handleStart);
      document.removeEventListener('mousedown', handleStart);
    };
  }, []);
};
